import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { formatCents } from '@/lib/format';
import type { Item } from '@everplan/shared';
import StatusChip from '@/components/StatusChip';
import InlineEdit from '@/components/InlineEdit';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

function formatMoneyRange(min: number, max: number): string {
  if (!min && !max) return '—';
  if (min === max) return formatCents(min);
  return `${formatCents(min)} – ${formatCents(max)}`;
}

export default function SortableItemRow({
  item,
  categoryId,
  onSelectItem,
  onUpdateItem,
  onToggleSelected,
  onRequestDeleteItem,
}: {
  item: Item & { _quotedMin?: number; _quotedMax?: number; _booked?: number };
  categoryId: string;
  onSelectItem: (id: string) => void;
  onUpdateItem: (itemId: string, data: { name?: string; estimatedCost?: number | null }) => void;
  onToggleSelected: (id: string, next: boolean) => void;
  onRequestDeleteItem: (item: Item) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: { type: 'item' as const, categoryId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/item border-b last:border-0 cursor-pointer transition-colors hover:bg-accent border-l-4 border-l-transparent',
        item.isSelectedForProject && 'bg-primary/5 border-l-primary',
      )}
      onClick={() => onSelectItem(item.id)}
    >
      <td className="px-2 py-2 w-8" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={item.isSelectedForProject}
          onCheckedChange={(v) => onToggleSelected(item.id, v === true)}
          aria-label={`Include ${item.name} in bestimate`}
        />
      </td>
      <td className="px-3 py-2 pl-5 font-medium text-foreground">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground/0 group-hover/item:text-muted-foreground/40 hover:!text-muted-foreground touch-none transition-colors"
            aria-label="Drag to reorder"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="size-3.5" />
          </button>
          <InlineEdit
            value={item.name}
            onSave={(name) => onUpdateItem(item.id, { name })}
          />
        </div>
      </td>
      <td className="px-3 py-2 text-foreground/70">{item.vendorContactName || '—'}</td>
      <td className="px-3 py-2 text-center"><StatusChip status={item.status} /></td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground/60">
        <InlineEdit
          value={String(item.estimatedCost ?? 0)}
          onSave={(v) => onUpdateItem(item.id, { estimatedCost: v ? parseInt(v, 10) : null })}
          type="currency"
          className="text-foreground/60 tabular-nums"
          inputClassName="text-right w-24"
        />
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {formatMoneyRange(item._quotedMin ?? 0, item._quotedMax ?? 0)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {item._booked ? formatCents(item._booked) : '—'}
      </td>
      <td className="px-2 py-2 text-right">
        <button
          type="button"
          aria-label={`Delete ${item.name}`}
          onClick={(e) => { e.stopPropagation(); onRequestDeleteItem(item); }}
          className="opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
        >
          <Trash2 className="size-3.5" />
        </button>
      </td>
    </tr>
  );
}
